import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { properties, Property } from '../../mocks/properties';

const STATE_OPTIONS = ['All States', 'CA', 'NV', 'AZ'];
const TYPE_OPTIONS = ['All Types', 'Single Family', 'Multi-Family', 'Condo', 'Commercial', 'Land'];
const BADGE_OPTIONS = ['All', 'NEW', 'AUCTION', 'HOT DEAL', 'PRICE DROP', 'FEATURED'];

const BADGE_COLORS: Record<string, string> = {
  'NEW': 'bg-green-600',
  'AUCTION': 'bg-red-600',
  'HOT DEAL': 'bg-gold text-navy-dark',
  'PRICE DROP': 'bg-navy',
  'FEATURED': 'bg-teal',
};

const STATE_COLORS: Record<string, string> = {
  CA: 'bg-amber-100 text-amber-700',
  NV: 'bg-red-100 text-red-700',
  AZ: 'bg-orange-100 text-orange-700',
};

const formatCurrency = (n: number) =>
  n >= 1000000 ? `$${(n / 1000000).toFixed(2)}M` : `$${(n / 1000).toFixed(0)}K`;

export default function PropertyListings() {
  const [stateFilter, setStateFilter] = useState('All States');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [badgeFilter, setBadgeFilter] = useState('All');
  const [sortBy, setSortBy] = useState<'equity' | 'price' | 'auction'>('equity');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() => {
    let list = [...properties];
    if (stateFilter !== 'All States') list = list.filter((p) => p.state === stateFilter);
    if (typeFilter !== 'All Types') list = list.filter((p) => p.propertyType === typeFilter);
    if (badgeFilter !== 'All') list = list.filter((p) => p.badge === badgeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          p.address.toLowerCase().includes(q) ||
          p.city.toLowerCase().includes(q) ||
          p.state.toLowerCase().includes(q)
      );
    }
    if (sortBy === 'equity') list.sort((a, b) => b.equityPct - a.equityPct);
    if (sortBy === 'price') list.sort((a, b) => a.currentBid - b.currentBid);
    if (sortBy === 'auction') list.sort((a, b) => a.auctionDays - b.auctionDays);
    return list;
  }, [stateFilter, typeFilter, badgeFilter, sortBy, searchQuery]);

  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* Page Hero */}
        <section className="relative bg-navy pt-40 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/6db9e2c2dbb0f91212288729cb449b76.jpg"
              alt="Property Listings"
              className="w-full h-full object-cover object-top opacity-15"
            />
          </div>
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
            <span className="inline-block px-4 py-1.5 border border-gold/50 text-gold text-xs font-semibold tracking-widest uppercase rounded-full mb-6">
              Live Opportunities
            </span>
            <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-4">
              Property Listings
            </h1>
            <p className="text-white/70 text-xl max-w-2xl">
              Browse {properties.length} exclusive tax-delinquent and pre-foreclosure properties across California, Nevada, and Arizona.
            </p>
          </div>
        </section>

        {/* Filter Bar */}
        <section className="bg-white border-b border-gray-100 sticky top-20 z-40">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4">
            <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-0">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search city, address, or state..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gold text-gray-700"
                />
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                {/* State Filter */}
                <div className="flex items-center gap-1 bg-cream rounded-lg p-1">
                  {STATE_OPTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStateFilter(s)}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        stateFilter === s ? 'bg-navy text-white' : 'text-gray-500 hover:text-navy'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>

                {/* Type */}
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:border-gold cursor-pointer bg-white"
                >
                  {TYPE_OPTIONS.map((t) => <option key={t}>{t}</option>)}
                </select>

                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'equity' | 'price' | 'auction')}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:border-gold cursor-pointer bg-white"
                >
                  <option value="equity">Sort: Highest Equity</option>
                  <option value="price">Sort: Lowest Bid</option>
                  <option value="auction">Sort: Auction Soon</option>
                </select>
              </div>

              <span className="text-sm text-gray-400 whitespace-nowrap">{filtered.length} properties</span>
            </div>

            {/* Badge Filters */}
            <div className="flex gap-2 mt-3 flex-wrap">
              {BADGE_OPTIONS.map((b) => (
                <button
                  key={b}
                  onClick={() => setBadgeFilter(b)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    badgeFilter === b
                      ? 'bg-gold text-navy-dark'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Properties Grid */}
        <section className="bg-cream py-12">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            {filtered.length === 0 ? (
              <div className="text-center py-24">
                <i className="ri-building-2-line text-gray-300 text-6xl mb-4 block" />
                <p className="text-gray-500 text-lg">No properties match your current filters.</p>
                <button
                  onClick={() => { setStateFilter('All States'); setTypeFilter('All Types'); setBadgeFilter('All'); setSearchQuery(''); }}
                  className="mt-4 text-gold text-sm font-semibold cursor-pointer hover:underline"
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filtered.map((prop: Property) => (
                  <div
                    key={prop.id}
                    className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-gold/30 transition-all duration-300 cursor-pointer"
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={prop.imageUrl}
                        alt={prop.address}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      <span className={`absolute top-3 right-3 px-2.5 py-1 text-xs font-bold rounded-md whitespace-nowrap text-white ${BADGE_COLORS[prop.badge]}`}>
                        {prop.badge === 'AUCTION' ? `AUCTION: ${prop.auctionDays}D` : prop.badge}
                      </span>
                      <span className={`absolute top-3 left-3 px-2 py-1 text-xs font-bold rounded-md ${STATE_COLORS[prop.state]}`}>
                        {prop.state}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="p-5">
                      <p className="text-navy font-semibold text-sm mb-0.5 truncate">{prop.address}</p>
                      <p className="text-gray-400 text-xs flex items-center gap-1 mb-4">
                        <i className="ri-map-pin-line text-gold text-xs" />
                        {prop.city}, {prop.state} {prop.zipCode}
                      </p>

                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-cream rounded-lg p-2.5">
                          <p className="text-gray-400 text-xs">Market Value</p>
                          <p className="text-navy font-bold text-sm mt-0.5">{formatCurrency(prop.marketValue)}</p>
                        </div>
                        <div className="bg-gold/10 rounded-lg p-2.5">
                          <p className="text-gray-400 text-xs">Current Bid</p>
                          <p className="text-gold font-bold text-sm mt-0.5">{formatCurrency(prop.currentBid)}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-gray-400 mb-4">
                        {prop.bedrooms > 0 ? (
                          <>
                            <span><i className="ri-home-4-line mr-1" />{prop.bedrooms} bd / {prop.bathrooms} ba</span>
                            <span><i className="ri-ruler-line mr-1" />{prop.sqft.toLocaleString()} sqft</span>
                          </>
                        ) : (
                          <>
                            <span><i className="ri-store-2-line mr-1" />Commercial</span>
                            <span><i className="ri-ruler-line mr-1" />{prop.sqft.toLocaleString()} sqft</span>
                          </>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                        <span className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                          <i className="ri-arrow-up-line" />
                          {prop.equityPct.toFixed(1)}% Equity
                        </span>
                        <Link
                          to="/membership-benefits"
                          className="text-xs text-gold font-semibold hover:underline cursor-pointer whitespace-nowrap"
                        >
                          View Details →
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Member CTA Banner */}
        <section className="bg-navy py-16">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
            <p className="text-white/60 text-sm uppercase tracking-widest mb-3">Members Only</p>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-4">
              Unlock 2,847+ Exclusive Property Listings
            </h2>
            <p className="text-white/60 mb-8 max-w-lg mx-auto">
              Members get 30–90 days early access before properties hit public auctions. Start your free trial today.
            </p>
            <Link
              to="/membership-benefits"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-navy-dark font-bold rounded-lg hover:bg-gold-light transition-colors whitespace-nowrap"
            >
              Start Free Trial <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
